import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Tab,
  Tabs,
  Typography,
  TextField
} from "@mui/material";
import { listMcpTools } from "../../services/functionsClient";

const formatMcpToolDocs = (tools = [], remoteUrl = "") => {
  if (!Array.isArray(tools) || !tools.length) return "";
  return tools
    .map((tool) => {
      const paramsMdList = [];
      const schema = tool.inputSchema?.properties || {};
      const required = tool.inputSchema?.required || [];
      Object.entries(schema).forEach(([paramName, props]) => {
        const typeStr = (props.type || "Any").replace("integer", "int").replace("string", "str");
        const reqStr = required.includes(paramName) ? "required" : "optional";
        const description = props.description || "";
        paramsMdList.push(`- \`${paramName}\` (${typeStr}, ${reqStr}): ${description}`);
      });
      const paramsMd = paramsMdList.join("\n") || "This tool takes no parameters.";
      return `### Tool: \`${tool.name}\`\n**Description**: ${tool.description || ""}\n\n**Parameters**:\n${paramsMd}\n\n**How to call**:\nUse mcpc[\`${remoteUrl}\`] to call \`${tool.name}\`.\n`;
    })
    .join("\n");
};

const ToolsSection = ({
  swaggerUrl,
  setSwaggerUrl,
  swaggerSpec,
  mcpServers,
  setMcpServers,
  remoteMcpDocs,
  setRemoteMcpDocs,
  handleSwaggerSpecUpload,
  handleRemoteMcpDocsUpload,
  onSaveToolSources
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localSwaggerUrl, setLocalSwaggerUrl] = useState(swaggerUrl || "");
  const [mcpUrl, setMcpUrl] = useState("");
  const [activeTab, setActiveTab] = useState("swagger");
  const [availableTools, setAvailableTools] = useState([]);
  const [selectedToolNames, setSelectedToolNames] = useState(new Set());
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolError, setToolError] = useState("");
  const [savingTools, setSavingTools] = useState(false);

  useEffect(() => {
    if (!dialogOpen) {
      setMcpUrl("");
      setAvailableTools([]);
      setSelectedToolNames(new Set());
      setToolError("");
      setLoadingTools(false);
      setActiveTab("swagger");
    }
  }, [dialogOpen]);

  const parsedMcpServers = useMemo(() => {
    if (!Array.isArray(mcpServers)) return [];
    return mcpServers
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === "string") return { url: entry, name: "" };
        if (typeof entry === "object") {
          const url = entry.url || entry.remoteUrl || entry.serverUrl || "";
          const name = entry.name || "";
          if (!url) return null;
          return { url, name };
        }
        return null;
      })
      .filter(Boolean);
  }, [mcpServers]);

  const associatedTools = useMemo(() => {
    const list = [];
    if (swaggerUrl || swaggerSpec) {
      list.push({
        title: "Swagger/OpenAPI",
        subtitle: swaggerUrl || "Provided via upload",
        description: swaggerSpec ? "Spec available" : "Using remote specification"
      });
    }
    parsedMcpServers.forEach((server) => {
      list.push({
        title: "Remote MCP Server",
        subtitle: server.name ? `${server.name} (${server.url})` : server.url,
        description: remoteMcpDocs ? "Docs attached" : "No docs added yet"
      });
    });
    return list;
  }, [parsedMcpServers, remoteMcpDocs, swaggerSpec, swaggerUrl]);

  useEffect(() => {
    setLocalSwaggerUrl(swaggerUrl || "");
  }, [swaggerUrl]);

  const handleCheckboxChange = (toolName) => {
    setSelectedToolNames((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) {
        next.delete(toolName);
      } else {
        next.add(toolName);
      }
      return next;
    });
  };

  const fetchMcpTools = async () => {
    const trimmedUrl = mcpUrl.trim();
    if (!trimmedUrl) return;
    setLoadingTools(true);
    setToolError("");
    try {
      const response = await listMcpTools({ mcpUrl: trimmedUrl });
      setAvailableTools(response?.tools || []);
    } catch (err) {
      setAvailableTools([]);
      setToolError(err.message || "Unable to load tools.");
    } finally {
      setLoadingTools(false);
    }
  };

  const handleSave = async () => {
    setToolError("");

    const trimmedSwaggerUrl = localSwaggerUrl.trim();
    setSwaggerUrl(trimmedSwaggerUrl);

    const trimmedMcpUrl = mcpUrl.trim();
    let updatedServers = parsedMcpServers;
    let updatedRemoteDocs = remoteMcpDocs;

    if (trimmedMcpUrl) {
      const selectedTools = availableTools.length && selectedToolNames.size
        ? availableTools.filter((tool) => selectedToolNames.has(tool.name))
        : [];

      const newServers = selectedTools.length
        ? selectedTools.map((tool) => ({ url: trimmedMcpUrl, name: tool.name }))
        : [{ url: trimmedMcpUrl, name: "" }];

      const dedupedServers = new Map();
      parsedMcpServers.forEach((server) => {
        dedupedServers.set(`${server.url}::${server.name || ""}`, server);
      });
      newServers.forEach((server) => {
        dedupedServers.set(`${server.url}::${server.name || ""}`, server);
      });

      updatedServers = Array.from(dedupedServers.values());

      setMcpServers(updatedServers);

      if (selectedTools.length) {
        updatedRemoteDocs = formatMcpToolDocs(selectedTools, trimmedMcpUrl);
        setRemoteMcpDocs(updatedRemoteDocs);
      }
    }

    if (onSaveToolSources) {
      setSavingTools(true);
      try {
        await onSaveToolSources({
          swaggerUrl: trimmedSwaggerUrl,
          mcpServers: updatedServers,
          remoteMcpDocs: updatedRemoteDocs
        });
      } catch (err) {
        setToolError(err.message || "Failed to save tool selections.");
        setSavingTools(false);
        return;
      }
      setSavingTools(false);
    }

    setDialogOpen(false);
  };

  const swaggerTab = (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Typography variant="subtitle1">Swagger/OpenAPI</Typography>
      <Typography variant="body2" color="text.secondary">
        Link a Swagger/OpenAPI spec via URL or upload a file.
      </Typography>
      <TextField
        value={localSwaggerUrl}
        onChange={(e) => setLocalSwaggerUrl(e.target.value)}
        placeholder="https://example.com/openapi.json"
        label="Swagger/OpenAPI URL"
        size="small"
        fullWidth
      />
      <Button variant="outlined" component="label" size="small" sx={{ alignSelf: "flex-start" }}>
        Upload Swagger/OpenAPI File
        <input type="file" accept=".json,.yaml,.yml,.txt" hidden onChange={handleSwaggerSpecUpload} />
      </Button>
    </Stack>
  );

  const mcpTab = (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Typography variant="subtitle1">Remote MCP Server</Typography>
      <Typography variant="body2" color="text.secondary">
        Add a remote MCP server and select the tools you want to include.
      </Typography>
      <TextField
        value={mcpUrl}
        onChange={(e) => setMcpUrl(e.target.value)}
        placeholder="https://example.com/mcp"
        label="Remote MCP Server URL"
        size="small"
        fullWidth
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" onClick={fetchMcpTools} disabled={!mcpUrl || loadingTools}>
          {loadingTools ? "Loading..." : "Fetch Tools"}
        </Button>
        <Button variant="outlined" component="label" size="small">
          Upload MCP Tool Docs
          <input type="file" accept=".md,.txt" hidden onChange={handleRemoteMcpDocsUpload} />
        </Button>
      </Stack>
      {loadingTools && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {toolError && <Alert severity="error">{toolError}</Alert>}
      {!loadingTools && !toolError && availableTools.length === 0 && mcpUrl && (
        <Typography sx={{ my: 1 }} color="text.secondary">
          No tools found on this server.
        </Typography>
      )}
      {!loadingTools && !toolError && availableTools.length > 0 && (
        <FormGroup>
          {availableTools.map((tool) => (
            <FormControlLabel
              key={tool.name}
              control={
                <Checkbox
                  checked={selectedToolNames.has(tool.name)}
                  onChange={() => handleCheckboxChange(tool.name)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{tool.name}</Typography>
                  {tool.description && (
                    <Typography variant="caption" color="text.secondary">
                      {tool.description}
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </FormGroup>
      )}
    </Stack>
  );

  return (
    <div className="grid" style={{ gap: "12px" }}>
      <div className="section-header" style={{ justifyContent: "space-between" }}>
        <div>
          <h4 style={{ margin: 0 }}>Associated Tools</h4>
          <p className="muted" style={{ margin: 0 }}>
            These are the tool sources connected to this agent.
          </p>
        </div>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Add New Tool
        </Button>
      </div>

      {associatedTools.length === 0 ? (
        <div className="panel" style={{ padding: "16px" }}>
          <p className="muted" style={{ margin: 0 }}>No tools added yet.</p>
        </div>
      ) : (
        <div className="grid" style={{ gap: "8px" }}>
          {associatedTools.map((tool) => (
            <div key={`${tool.title}-${tool.subtitle}`} className="panel" style={{ padding: "12px" }}>
              <div style={{ fontWeight: 600 }}>{tool.title}</div>
              <div className="muted" style={{ fontSize: "0.9rem" }}>{tool.subtitle}</div>
              <div style={{ marginTop: "4px", fontSize: "0.9rem" }}>{tool.description}</div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add a Tool Source</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            aria-label="tool source tabs"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Swagger/OpenAPI" value="swagger" />
            <Tab label="MCP" value="mcp" />
          </Tabs>

          {activeTab === "swagger" && swaggerTab}
          {activeTab === "mcp" && mcpTab}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loadingTools || savingTools}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ToolsSection;
